<template>
  <b-card
    no-body
    class="card-revenue-budget"
  >
    <b-row class="mx-0">
      <b-col
        md="12"
        class="revenue-report-wrapper"
      >

        <b-card-header>
          <b-card-title>
            {{ $t('Customer Growth Analytics') }}
          </b-card-title>

          <feather-icon
            icon="RotateCwIcon"
            class="cursor-pointer"
            @click="fetchCustomerGrowth(defaultYear)"
          />
        </b-card-header>

        <b-row>
          <b-col
            md="3"
            offset="9"
          >
            <SelectField
              id="year"
              v-model="defaultYear"
              label=""
              :options="yearOptions"
              :reduce="option => option"
              item="label"
              placeholder="Select"
              :set-selected="onSelectYear"
            />
          </b-col>
        </b-row>

        <!-- chart -->
        <vue-apex-charts
          v-if="lineChartSimple.series[0].data.length"
          ref="lineChart"
          type="line"
          height="400"
          :options="lineChartSimple.chartOptions"
          :series="lineChartSimple.series"
        />
      </b-col>
    </b-row>

    <Spinner :show="isLoadingCustomerGrowth" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BCardHeader, BCardTitle,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import Ripple from 'vue-ripple-directive'
import { onMounted, computed, ref } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import Spinner from '@/views/components/spinner/Spinner.vue'
import useToastAlert from '../../../composable/useToast'
import SelectField from '../../forms/SelectField.vue'
import toNumber from '../../../helpers/toNumber'

export default {
  components: {
    VueApexCharts,
    BCard,
    BRow,
    BCol,
    BCardHeader,
    BCardTitle,
    SelectField,
    Spinner,
  },
  directives: {
    Ripple,
  },
  setup() {
    const { getCustomerGrowthAnalytics } = useActions({
      getCustomerGrowthAnalytics: 'dashboard/getCustomerGrowthAnalytics',
    })
    const { t } = useI18nUtils()
    const { toastAlert } = useToastAlert()

    const isLoadingCustomerGrowth = ref(false)
    const defaultYear = ref('')
    const yearOptions = ref([])
    const customerData = ref([])

    // Computed property for translated months
    const defaultMonths = computed(() => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => t(month)))

    // Computed property for chart options
    const lineChartSimple = computed(() => ({
      series: [
        {
          name: t('Customers'),
          data: customerData.value.map(item => ({
            y: item.totalCount,
            x: t(item.reportMonth),
            percentageGrowth: item.percentageGrowth,
          })),
        },
      ],
      chartOptions: {
        chart: {
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        markers: {
          strokeWidth: 7,
          strokeOpacity: 1,
          strokeColors: [$themeColors.light],
          colors: [$themeColors.warning],
        },
        colors: [$themeColors.warning],
        dataLabels: { enabled: false },
        stroke: { curve: 'straight' },
        grid: { xaxis: { lines: { show: true } } },
        tooltip: {
          custom({ seriesIndex, dataPointIndex, w }) {
            const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
            return `
              ${'<div class="px-1 py-50"><span>'}
               ${toNumber(data.y)} (${data.percentageGrowth}%)
            </span></div>`
          },
        },
        xaxis: {
          categories: customerData.value.length
            ? customerData.value.map(item => t(item.reportMonth))
            : defaultMonths.value,
        },
      },
    }))

    const fetchCustomerGrowth = async (value = '') => {
      try {
        isLoadingCustomerGrowth.value = true
        const year = value || defaultYear.value
        const { status, data } = await getCustomerGrowthAnalytics({ year })

        if (status === 200 && Object.entries(data).length) {
          yearOptions.value = data.years
          const { years } = data
          const sortedYears = years.sort((a, b) => b - a)
          const [defaultYearValue] = sortedYears

          if (value) {
            defaultYear.value = sortedYears.find(item => item === value)
          } else {
            defaultYear.value = defaultYearValue // Kay needs to send a default year
          }
          customerData.value = data.summary
        } else {
          customerData.value = [] // Reset data if the response is invalid
        }
      } catch (err) {
        toastAlert(err)
      } finally {
        isLoadingCustomerGrowth.value = false
      }
    }

    const onSelectYear = async value => {
      await fetchCustomerGrowth(value)
    }

    onMounted(() => {
      fetchCustomerGrowth()
    })

    return {
      isLoadingCustomerGrowth,
      lineChartSimple,
      yearOptions,
      defaultYear,
      fetchCustomerGrowth,
      onSelectYear,
    }
  },
}
</script>
